/**
 * VeoOptionsPanel - Collapsible settings panel for Veo AI video generation.
 * Shows quality, resolution, aspect ratio, audio toggle, and cost estimate.
 */

import { Sparkles, ChevronDown, ChevronUp, Monitor, Smartphone, Square } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import {
  VEO_QUALITY_OPTIONS,
  VEO_RESOLUTION_OPTIONS,
  VEO_ASPECT_RATIO_OPTIONS,
  usdToTokens,
  veoTokensPerSecond,
  type VeoConfig,
} from '@/constants/videoSources';
import { CompactSelect } from '../../common/CompactSelect';
import type { CompactOption } from '../../common/CompactSelect';
import { SegmentedPicker } from '../../common/SegmentedPicker';
import type { SegmentOption } from '../../common/SegmentedPicker';

interface VeoOptionsPanelProps {
  veoConfig: VeoConfig;
  /** Upstream USD cost of one clip, or `null` when the configuration has no price. */
  estimatedCost: number | null;
  durationMinutes: number;
  showAdvanced: boolean;
  onToggleAdvanced: () => void;
  onChange: (config: VeoConfig) => void;
  disabled: boolean;
  /** Hide cost estimate (e.g. when parent already shows unified cost) */
  showCost?: boolean;
}

const ASPECT_RATIO_ICONS: Record<string, JSX.Element> = {
  '16:9': <Monitor className="h-4 w-4" />,
  '9:16': <Smartphone className="h-4 w-4" />,
  '1:1': <Square className="h-4 w-4" />,
};

export function VeoOptionsPanel({
  veoConfig,
  estimatedCost,
  durationMinutes,
  showAdvanced,
  onToggleAdvanced,
  onChange,
  disabled,
  showCost = true,
}: VeoOptionsPanelProps) {
  const { t } = useTranslation();

  // The per-second rate depends on BOTH quality and resolution -- 4K is its own
  // rate in Google's table, not a surcharge on 1080p -- so it is asked for at the
  // resolution currently selected rather than read off a fixed field.
  const qualityOptions: SegmentOption[] = VEO_QUALITY_OPTIONS.map((option) => ({
    value: option.value,
    label: option.label,
    description: option.description,
    trailingMeta: `${veoTokensPerSecond(option.value, veoConfig.resolution)} tokens/s`,
  }));

  const aspectRatioOptions: SegmentOption[] = VEO_ASPECT_RATIO_OPTIONS.map((option) => ({
    value: option.value,
    label: option.value,
    iconNode: ASPECT_RATIO_ICONS[option.value],
  }));

  return (
    <div className="border border-white/10 rounded-lg overflow-hidden bg-black/20">
      <button
        type="button"
        onClick={onToggleAdvanced}
        className="w-full flex items-center justify-between p-3 bg-black/20 hover:bg-white/5 transition-colors"
      >
        <span className="font-medium text-white/80 flex items-center space-x-2">
          <Sparkles className="h-4 w-4 text-primary-400" />
          <span>{t('videoSource.veoSettings', { defaultValue: 'AI Video Settings' })}</span>
        </span>
        {showAdvanced ? (
          <ChevronUp className="w-5 h-5 text-white/40" />
        ) : (
          <ChevronDown className="w-5 h-5 text-white/40" />
        )}
      </button>

      {showAdvanced && (
        <div className="p-4 space-y-4 border-t border-white/5">
          {/* Quality */}
          <SegmentedPicker
            label={t('videoSource.quality', { defaultValue: 'Quality' })}
            options={qualityOptions}
            value={veoConfig.quality}
            onChange={(val) =>
              !disabled && onChange({ ...veoConfig, quality: val as VeoConfig['quality'] })
            }
            size="md"
            layout="equal"
            disabled={disabled}
          />

          {/* Resolution */}
          <CompactSelect
            label={t('videoSource.resolution', { defaultValue: 'Resolution' })}
            options={VEO_RESOLUTION_OPTIONS.map<CompactOption>((option) => ({
              value: option.value,
              label: option.label,
              hint: `${veoTokensPerSecond(veoConfig.quality, option.value)} tokens/s`,
            }))}
            value={veoConfig.resolution}
            onChange={(val) =>
              !disabled &&
              onChange({
                ...veoConfig,
                resolution: val as VeoConfig['resolution'],
              })
            }
            disabled={disabled}
          />

          {/* Aspect Ratio */}
          <SegmentedPicker
            label={t('videoSource.aspectRatio', { defaultValue: 'Aspect Ratio' })}
            options={aspectRatioOptions}
            value={veoConfig.aspect_ratio}
            onChange={(val) =>
              !disabled &&
              onChange({
                ...veoConfig,
                aspect_ratio: val as VeoConfig['aspect_ratio'],
              })
            }
            size="sm"
            layout="equal"
            disabled={disabled}
          />

          {/* Enable Audio */}
          <div className="flex items-center justify-between">
            <label className="label-inline">
              {t('videoSource.enableAudio', { defaultValue: 'Generate Audio' })}
            </label>
            <button
              type="button"
              onClick={() =>
                !disabled &&
                onChange({
                  ...veoConfig,
                  enable_audio: !veoConfig.enable_audio,
                })
              }
              disabled={disabled}
              className={[
                'relative w-11 h-6 rounded-full transition-colors',
                veoConfig.enable_audio ? 'bg-primary-500' : 'bg-white/10',
                disabled ? 'opacity-50 cursor-not-allowed' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <span
                className={[
                  'absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform',
                  veoConfig.enable_audio ? 'translate-x-5' : 'translate-x-0',
                ].join(' ')}
              />
            </button>
          </div>

          {/* Cost Estimate */}
          {showCost && (
            <div className="mt-4 text-right">
              <div className="text-xs text-white/40">
                {t('videoSource.estimatedCost', { defaultValue: 'Cost' })}
              </div>
              <div className="text-lg font-semibold text-green-400">
                {estimatedCost === null
                  ? t('videoSource.priceUnavailable', { defaultValue: 'No price yet' })
                  : `${usdToTokens(estimatedCost).toLocaleString()} tokens`}
              </div>
              <div className="text-xs text-white/40 mt-0.5">
                {Math.ceil(durationMinutes * 60)}s x{' '}
                {veoTokensPerSecond(veoConfig.quality, veoConfig.resolution)} tokens/s
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
